import { create } from 'zustand';
import { Team, TeamCreateRequest, TeamUpdateRequest } from '../types/team.type';
import {
  getTeams, createTeam, updateTeam, deleteTeam,
  addEmployeeToTeam, removeEmployeeFromTeam,
} from '../services/team.service';

function normalizeTeam(raw: Team): Team {
  return {
    ...raw,
    include_manager_tasks: raw.include_manager_tasks ?? false,
    allowed_space_names: raw.allowed_space_names ?? [],
  };
}

interface TeamState {
  teams: Team[];
  isLoading: boolean;
  error: string | null;
  fetchTeams: () => Promise<void>;
  addTeam: (data: TeamCreateRequest) => Promise<Team>;
  editTeam: (id: string, data: TeamUpdateRequest) => Promise<Team>;
  removeTeam: (id: string) => Promise<void>;
  addMember: (teamId: string, employeeId: string) => Promise<void>;
  removeMember: (teamId: string, employeeId: string) => Promise<void>;
}

export const useTeamStore = create<TeamState>((set) => ({
  teams: [],
  isLoading: false,
  error: null,

  fetchTeams: async () => {
    set({ isLoading: true, error: null });
    try {
      const res = await getTeams();
      set({ teams: (res.data ?? []).map(normalizeTeam), isLoading: false });
    } catch (e: any) {
      set({ error: e.message || 'Failed to load teams', isLoading: false });
    }
  },

  addTeam: async (data: TeamCreateRequest) => {
    const t = normalizeTeam(await createTeam(data));
    set(s => ({ teams: [t, ...s.teams] }));
    return t;
  },

  editTeam: async (id: string, data: TeamUpdateRequest) => {
    const updated = normalizeTeam(await updateTeam(id, data));
    set(s => ({ teams: s.teams.map(t => t.id === id ? updated : t) }));
    return updated;
  },

  removeTeam: async (id: string) => {
    await deleteTeam(id);
    set(s => ({ teams: s.teams.filter(t => t.id !== id) }));
  },

  addMember: async (teamId: string, employeeId: string) => {
    await addEmployeeToTeam(teamId, employeeId);
  },

  removeMember: async (teamId: string, employeeId: string) => {
    await removeEmployeeFromTeam(teamId, employeeId);
  },
}));
